import type { AppetiteModel } from '@risksmart-app/domain/src/types/consts/appetite-model';
import type { RiskScoringModel } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import type {
  Appetite_Model_Enum,
  Risk_Scoring_Model_Enum,
  UpdateAggregationSettingsForOrgMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

// The web layer speaks the GraphQL-generated enums (what every call site uses);
// the tRPC procedure input is typed with the domain enums (z.nativeEnum). The
// string values are identical, so the public input takes the GraphQL enums and
// we cast to the domain enums at the single mutateAsync boundary below.
export interface UpdateAggregationSettingsForOrgInput {
  RiskScoringModel?: Risk_Scoring_Model_Enum | null;
  AppetiteCascadingModel?: Appetite_Model_Enum | null;
  Config?: Record<string, unknown> | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpdateAggregationSettingsForOrgMutation => ({
  insert_aggregation_org: {
    __typename: 'aggregation_org_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpdateAggregationSettingsForOrgTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.aggregation.updateAggregationSettingsForOrg.mutationOptions(
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.aggregation.getAggregationSettingsForOrg.queryKey(),
          });
        },
      }
    ),
  });

  return {
    updateAggregationSettingsForOrg: async (
      input: UpdateAggregationSettingsForOrgInput
    ): Promise<UpdateAggregationSettingsForOrgMutation> => {
      const result = await mutation.mutateAsync({
        RiskScoringModel:
          (input.RiskScoringModel ?? null) as RiskScoringModel | null,
        Appetite:
          (input.AppetiteCascadingModel ?? null) as AppetiteModel | null,
        Config: input.Config ?? null,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
